import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, AlertCircle, Home, Phone, Mail, Clock, Users, Calendar, Eye, EyeOff } from 'lucide-react';

interface WaiverData {
  id: string;
  qrCode: string;
  name: string;
  email: string;
  phone: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  signedAt: string;
  expiresAt: string;
  isValid: boolean;
  signedFor: string;
  numKids: number;
  kidsAges: number[];
  signatureUrl: string;
  marketingConsent: any;
  interests: string[];
}

const PublicWaiverConfirmation: React.FC = () => {
  const { qrCode } = useParams<{ qrCode: string }>();
  const [waiverData, setWaiverData] = useState<WaiverData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFullInfo, setShowFullInfo] = useState(false);

  // Privacy protection utilities
  const maskName = (name: string) => {
    if (!name) return '';
    if (showFullInfo) return name;
    const words = name.split(' ');
    if (words.length === 1) {
      return words[0].charAt(0) + '*'.repeat(Math.max(0, words[0].length - 1));
    }
    return words[0] + ' ' + words[1].charAt(0) + '*'.repeat(Math.max(0, words[1].length - 1));
  };

  const maskEmail = (email: string) => {
    if (!email) return 'Not provided';
    if (showFullInfo) return email;
    const [localPart, domain] = email.split('@');
    if (!domain) return email; // Invalid email format
    return localPart.charAt(0) + '*'.repeat(Math.max(0, localPart.length - 2)) + localPart.slice(-1) + '@' + domain;
  };

  const maskPhone = (phone: string) => {
    if (!phone) return 'Not provided';
    if (showFullInfo) return phone;
    // Show last 4 digits only
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 4) {
      return '***-***-' + digits.slice(-4);
    }
    return '***-***-****';
  };

  useEffect(() => {
    const fetchWaiverData = async () => {
      if (!qrCode) {
        setError('No QR code provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/waivers/confirmation/${qrCode}`);
        
        // Check if response is ok first
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check content type to ensure it's JSON
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Response is not JSON - received HTML instead");
        }

        const data = await response.json();

        if (data.exists && data.waiver) {
          setWaiverData(data.waiver);
        } else {
          setError('Waiver not found');
        }
      } catch (err) {
        console.error('Error fetching waiver data:', err);
        if (err instanceof Error) {
          if (err.message.includes('HTML instead')) {
            setError('Backend server not responding correctly. Please ensure the server is running on port 3001.');
          } else if (err.message.includes('Failed to fetch')) {
            setError('Cannot connect to backend server. Please ensure the server is running.');
          } else {
            setError(err.message);
          }
        } else {
          setError('An unexpected error occurred while fetching waiver data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWaiverData();
  }, [qrCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading waiver confirmation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !waiverData) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Waiver Not Found
            </h1>
            <p className="text-gray-600 mb-8">
              {error || 'The waiver confirmation could not be found or may have expired.'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const signedDate = new Date(waiverData.signedAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const expiryDate = new Date(waiverData.expiresAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const signedForDisplay = {
    'self': 'Self',
    'family': 'Family',
    'group': 'Group'
  }[waiverData.signedFor] || waiverData.signedFor;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              waiverData.isValid ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              {waiverData.isValid ? (
                <Check className="w-10 h-10 text-green-600" />
              ) : (
                <Clock className="w-10 h-10 text-yellow-600" />
              )}
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Waiver Confirmation
            </h1>
            
            <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
              waiverData.isValid 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {waiverData.isValid ? '✅ VALID WAIVER' : '⚠️ EXPIRED WAIVER'}
            </div>
          </div>

          {/* Privacy Toggle */}
          <div className="mb-6 flex justify-center">
            <button
              onClick={() => setShowFullInfo(!showFullInfo)}
              className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
            >
              {showFullInfo ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Full Details
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Show Full Details
                </>
              )}
            </button>
          </div>

          {/* Main Information */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Personal Information */}
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Personal Information
                {!showFullInfo && (
                  <span className="ml-2 px-2 py-1 text-xs bg-blue-200 text-blue-700 rounded-full">
                    Privacy Protected
                  </span>
                )}
              </h3>
              <div className="space-y-3 text-blue-800">
                <div className="flex items-start">
                  <strong className="w-20 flex-shrink-0">Name:</strong>
                  <span className="font-mono">{maskName(waiverData.name)}</span>
                </div>
                <div className="flex items-start">
                  <Mail className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                  <strong className="w-16 flex-shrink-0">Email:</strong>
                  <span className="font-mono text-sm break-all">{maskEmail(waiverData.email)}</span>
                </div>
                {waiverData.phone && (
                  <div className="flex items-start">
                    <Phone className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <strong className="w-16 flex-shrink-0">Phone:</strong>
                    <span className="font-mono">{maskPhone(waiverData.phone)}</span>
                  </div>
                )}
                <div className="flex items-start">
                  <strong className="w-20 flex-shrink-0">Signed for:</strong>
                  <span>{signedForDisplay}</span>
                </div>
                {waiverData.numKids > 0 && (
                  <div className="flex items-start">
                    <strong className="w-20 flex-shrink-0">Kids:</strong>
                    <span>{waiverData.numKids} child{waiverData.numKids > 1 ? 'ren' : ''} 
                      {waiverData.kidsAges.length > 0 && (
                        <span className="text-sm text-blue-600"> (ages: {waiverData.kidsAges.join(', ')})</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Waiver Details */}
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Waiver Details
              </h3>
              <div className="space-y-3 text-green-800">
                <div className="flex items-start">
                  <strong className="w-24 flex-shrink-0">Signed on:</strong>
                  <div className="text-sm">
                    <div className="font-medium">{signedDate}</div>
                    <div className="text-green-600 text-xs">
                      {new Date(waiverData.signedAt).toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex items-start">
                  <strong className="w-24 flex-shrink-0">Valid until:</strong>
                  <div className="text-sm">
                    <div className="font-medium">{expiryDate}</div>
                    <div className="text-green-600 text-xs">
                      {Math.ceil((new Date(waiverData.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days remaining
                    </div>
                  </div>
                </div>
                <div className="flex items-start">
                  <strong className="w-24 flex-shrink-0">QR Code:</strong>
                  <div className="text-sm">
                    <div className="font-mono bg-white px-2 py-1 rounded text-xs border border-green-200 break-all">
                      {waiverData.qrCode}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact (if provided) */}
          {(waiverData.emergencyContact || waiverData.emergencyPhone) && (
            <div className="bg-orange-50 rounded-lg p-6 mb-8 border border-orange-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Emergency Contact
                </h3>
                {!showFullInfo && (
                  <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs font-medium">
                    Privacy Protected
                  </span>
                )}
              </div>
              <div className="space-y-3 text-orange-800">
                {waiverData.emergencyContact && (
                  <div className="flex items-start">
                    <strong className="w-16 flex-shrink-0 text-sm">Contact:</strong>
                    <span className="text-sm">
                      {showFullInfo ? waiverData.emergencyContact : maskName(waiverData.emergencyContact)}
                    </span>
                  </div>
                )}
                {waiverData.emergencyPhone && (
                  <div className="flex items-start">
                    <strong className="w-16 flex-shrink-0 text-sm">Phone:</strong>
                    <span className="text-sm font-mono">
                      {showFullInfo ? waiverData.emergencyPhone : maskPhone(waiverData.emergencyPhone)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status Notice */}
          <div className={`rounded-lg border-l-4 p-6 mb-8 ${
            waiverData.isValid 
              ? 'bg-green-50 border-green-400' 
              : 'bg-yellow-50 border-yellow-400'
          }`}>
            <div className="flex items-center gap-3">
              {waiverData.isValid ? (
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
              ) : (
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">!</span>
                </div>
              )}
              <div>
                <h3 className={`text-lg font-semibold ${
                  waiverData.isValid ? 'text-green-800' : 'text-yellow-800'
                }`}>
                  {waiverData.isValid ? 'Valid Waiver' : 'Expired Waiver'}
                </h3>
                <div className={`mt-2 text-sm ${
                  waiverData.isValid ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  <p>
                    {waiverData.isValid 
                      ? 'This waiver is currently valid and the customer may participate in activities.'
                      : 'This waiver has expired. The customer must sign a new waiver before participating in activities.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Actions */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Staff Actions
            </h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => window.print()}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                🖨️ Print Confirmation
              </button>
              <Link
                to="/admin/dashboard"
                className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-center flex items-center justify-center gap-2"
              >
                🏠 Admin Dashboard
              </Link>
              <Link
                to="/"
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors text-center flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                Home
              </Link>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              This confirmation is automatically generated for staff verification purposes.
              For questions or assistance, please contact management.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicWaiverConfirmation;